import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/AuthContext.jsx';
import NurseCard from '../../components/NurseCard.jsx';
import { Loading, StatusPill, Empty, SectionHeader, Skeleton } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { formatDayLabel, timeRange, currency, STATUS_LABEL, firstName } from '../../lib/format.js';
import ServiceIcon from '../../components/ServiceIcon.jsx';
import ServiceBanner from '../../components/ServiceBanner.jsx';

/** Greeting that matches the clock — small touch, makes the app feel attended to. */
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function PatientHome() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [topNurses, setTopNurses] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [svc, nurses] = await Promise.all([
          api.get('/api/services'),
          api.get('/api/nurses', { sort: 'rating', pageSize: 6 }),
        ]);
        if (!alive) return;
        setServices(svc);
        setTopNurses(nurses.results);
        if (user?.role === 'patient') {
          const bookings = await api.get('/api/bookings', { status: 'pending,accepted' });
          if (alive) setUpcoming(bookings.slice(0, 3));
        }
      } catch {
        /* offline fallback handled by empty states */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  return (
    <>
      {/* ------------------------------------ Greeting + featured banner
          Both sit in the top band, side by side on desktop and stacked on a
          phone, so the first screen carries the personal greeting, the search
          box, and what the service actually offers. */}
      <div className="home-top">
        <section className="hero">
          <span className="hero-eyebrow">
            <Icon name="shield-check" />
            Every nurse licence-verified
          </span>
          <h1>{user ? `${greeting()}, ${firstName(user.name)}` : 'Care that comes to you'}</h1>
          <p>
            Book a verified home nurse by service, availability, distance and budget — confirmed in minutes,
            delivered at your doorstep.
          </p>

          <form
            className="hero-search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              navigate(`/search?q=${encodeURIComponent(query)}`);
            }}
          >
            <div className="input-icon">
              <Icon name="search" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search nurses…"
                aria-label="Search nurses"
              />
            </div>
            <button className="btn btn-primary btn-lg">
              Search <Icon name="arrow-right" />
            </button>
          </form>

          <div className="hero-trust">
            <div>
              <b>{services.length || '—'}</b>
              <span>Care services</span>
            </div>
            <div>
              <b>Cash</b>
              <span>Paid after the visit</span>
            </div>
            <div>
              <b>Free</b>
              <span>Cancellation</span>
            </div>
          </div>
        </section>

        <ServiceBanner services={services} />
      </div>

      {/* --------------------------------------------- Upcoming visits */}
      {upcoming.length > 0 && (
        <section className="mb-3">
          <SectionHeader title="Your upcoming visits" subtitle="Confirmed and awaiting confirmation.">
            <Link to="/bookings" className="btn btn-sm btn-ghost">
              View all <Icon name="arrow-right" />
            </Link>
          </SectionHeader>
          <div className="card">
            {upcoming.map((b) => (
              <Link key={b.id} to={`/bookings/${b.id}`} className="list-row">
                <span className="icon-tile" aria-hidden="true">
                  {b.service_icon || <Icon name="stethoscope" />}
                </span>
                <div className="grow">
                  <div className="list-title truncate">
                    {b.service_name} with {b.nurse_name}
                  </div>
                  <div className="list-sub truncate">
                    {formatDayLabel(b.date)} · {timeRange(b.start_time, b.end_time)} · {currency(b.amount)}
                  </div>
                </div>
                <StatusPill status={b.status} label={STATUS_LABEL[b.status]} />
                <span className="list-chevron">
                  <Icon name="chevron-right" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* -------------------------------------------------- Services */}
      <section className="mb-3">
        <SectionHeader title="What do you need help with?" subtitle="Pick a service to see nurses who provide it." />
        {loading ? (
          <div className="grid grid-4">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="service-tile">
                <Skeleton w={48} h={48} r={14} />
                <Skeleton w="70%" h={11} style={{ marginTop: 10 }} />
                <Skeleton w="45%" h={9} style={{ marginTop: 6 }} />
              </div>
            ))}
          </div>
        ) : services.length === 0 ? (
          <Empty art="no-results" title="No services listed yet">
            Service categories will appear here once an administrator adds them.
          </Empty>
        ) : (
          <div className="grid grid-4">
            {services.map((s, i) => (
              <Link key={s.id} to={`/search?serviceId=${s.id}`} className="service-tile anim-rise" style={{ '--i': i }}>
                <span className="ico" aria-hidden="true">
                  <ServiceIcon service={s} size={26} />
                </span>
                <span className="nm">{s.name}</span>
                <span className="ct">
                  {s.nurse_count} nurse{s.nurse_count === 1 ? '' : 's'}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ----------------------------------------------- Top nurses */}
      <section>
        <SectionHeader title="Top rated nurses near you" subtitle="Every profile below is licence-verified by our team.">
          <Link to="/search" className="btn btn-sm btn-ghost">
            Browse all <Icon name="arrow-right" />
          </Link>
        </SectionHeader>
        {loading ? (
          <Loading rows={3} variant="card" />
        ) : topNurses.length === 0 ? (
          <Empty
            art="no-results"
            title="No nurses available yet"
            action={
              <Link to="/search" className="btn btn-primary">
                Browse all nurses
              </Link>
            }
          >
            Once nurses are verified by our team they will appear here.
          </Empty>
        ) : (
          <div className="nurse-grid">
            {topNurses.map((n, i) => (
              <NurseCard key={n.id} nurse={n} index={i} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
